import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Loader2, CheckCircle, XCircle, AlertTriangle,
  ShieldCheck, ShieldAlert, ShieldX, ExternalLink, Globe,
  RefreshCw, Download, FileText, ChevronDown, ChevronUp,
  Lock, Eye, Filter, ArrowUpDown, Gavel,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '@/components/ui/accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import api, { ley40GateAPI } from '@/lib/api';
import { cn } from '@/lib/utils';
import { useAuth } from '@/lib/auth-context';
import { useTranslation } from '@/i18n/TranslationContext';

function getScoreInfo(score) {
  if (score >= 80) return { label: 'Compliant', color: 'text-vrs-green', bg: 'bg-vrs-green', icon: ShieldCheck, borderColor: 'border-vrs-green/30', bgLight: 'bg-vrs-green/10' };
  if (score >= 60) return { label: 'At Risk', color: 'text-vrs-amber', bg: 'bg-vrs-amber', icon: ShieldAlert, borderColor: 'border-vrs-amber/30', bgLight: 'bg-vrs-amber/10' };
  return { label: 'Non-compliant', color: 'text-vrs-red', bg: 'bg-vrs-red', icon: ShieldX, borderColor: 'border-vrs-red/30', bgLight: 'bg-vrs-red/10' };
}

function getMaturity(score) {
  if (score >= 91) return { level: 'Excellent', color: 'text-vrs-green', bg: 'bg-vrs-green/10', border: 'border-vrs-green/30' };
  if (score >= 71) return { level: 'High', color: 'text-vrs-cyan', bg: 'bg-vrs-cyan/10', border: 'border-vrs-cyan/30' };
  if (score >= 41) return { level: 'Moderate', color: 'text-vrs-amber', bg: 'bg-vrs-amber/10', border: 'border-vrs-amber/30' };
  return { level: 'Low', color: 'text-vrs-red', bg: 'bg-vrs-red/10', border: 'border-vrs-red/30' };
}

function formatDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function SeverityBadge({ type }) {
  const styles = {
    error: 'border-vrs-red/30 text-vrs-red bg-vrs-red/10',
    warning: 'border-vrs-amber/30 text-vrs-amber bg-vrs-amber/10',
    notice: 'border-vrs-cyan/30 text-vrs-cyan bg-vrs-cyan/10',
    High: 'border-vrs-red/30 text-vrs-red bg-vrs-red/10',
    Medium: 'border-vrs-amber/30 text-vrs-amber bg-vrs-amber/10',
    Low: 'border-vrs-cyan/30 text-vrs-cyan bg-vrs-cyan/10',
    Informational: 'border-muted-foreground/30 text-muted-foreground bg-muted/10',
    compliant: 'border-vrs-green/30 text-vrs-green bg-vrs-green/10',
    non_compliant: 'border-vrs-red/30 text-vrs-red bg-vrs-red/10',
    unable_to_determine: 'border-vrs-amber/30 text-vrs-amber bg-vrs-amber/10',
  };
  return <Badge variant="outline" className={cn('text-[10px] capitalize', styles[type] || styles.notice)}>{type?.replace(/_/g, ' ')}</Badge>;
}

// [Phase 3(c) Stage 1, §5] Friendly label for a stored scan-failure record
// (status:"error"). Distinct from non-compliance and from not-in-scope.
function scanFailureLabel(errorClass) {
  switch (errorClass) {
    case 'browser_unavailable': return 'Scanner temporarily unavailable — please retry';
    case 'internal_error': return 'Internal error during scan — please retry';
    default: return 'Scan did not complete — please retry';
  }
}

// ---------------------------------------------------------------------------
// [Phase 3(c) Stage 3.5] Applicability + stratified-rendering helpers.
// All display-only. Status strings mirror ley40_condition_adapter.py exactly.
// No legal basis is asserted for WCAG or Ley 39 (Option 1 / DEC-014); no
// statute or citation appears in the baseline captions.
// ---------------------------------------------------------------------------

// Friendly display name per resolver law_key — DISPLAY ONLY (never selection).
function lawKeyDisplayName(lawKey) {
  switch (lawKey) {
    case 'wcag_2_1': return 'Accessibility (WCAG 2.1)';
    case 'ley_39_2012_pr': return 'Privacy Notice (Ley 39-2012)';
    case 'ley_40_2024_pr': return 'Ley 40-2024 Cybersecurity';
    case 'hipaa': return 'HIPAA';
    case 'glba': return 'GLBA';
    case 'cmmc': return 'CMMC';
    default: return lawKey;
  }
}

// Neutral, non-blaming phrase for why a law was NOT scanned (manifest reason).
function exclusionReasonLabel(reason) {
  switch (reason) {
    case 'entity_gate_no': return 'Not in scope for your entity based on your applicability answers.';
    case 'entity_gate_unanswered': return 'Applicability not yet determined — complete your applicability profile.';
    case 'no_profile_trigger': return 'Not applicable to your current profile.';
    case 'law_suspended': return 'Not currently offered.';
    case 'law_not_approved': return 'Not currently offered.';
    case 'not_scanned_out_of_scope': return 'Outside your declared covered-systems scope for this scan.';
    case 'per_scan_override_exclude': return 'Excluded by your selection for this scan.';
    case 'gate_unavailable': return 'Applicability could not be evaluated.';
    default: return 'Not scanned.';
  }
}

// The WCAG baseline caption (Option 1, DEC-014) — TWO clauses, NO citation.
const WCAG_BASELINE_CAPTION_A =
  'WCAG 2.1 is a technical standard scanned here as a baseline. Whether it legally applies to your entity has not been individually assessed.';
const WCAG_BASELINE_CAPTION_B =
  'Automated accessibility scanning is neither exhaustive nor definitive — it can miss real barriers and flag non-issues, and is not a substitute for a manual audit.';
// Ley 39 baseline caption = applicability clause only (no scanner-limits clause).
const LEY39_BASELINE_CAPTION =
  'This privacy-notice check runs as a baseline. Whether this instrument legally applies to your entity has not been individually assessed.';

// MFA signal (7(8)) — weak external signal, never scored (D-6).
function mfaSignalLabel(mfa) {
  switch (mfa) {
    case 'signal_positive': return 'weak external signal: present';
    case 'signal_negative': return 'weak external signal: not detected';
    default: return 'weak external signal: undetermined';
  }
}

// Human label for a per-condition status. Pending statuses are "awaiting …",
// never failures; 7(6)/structural and cannot_determine render as not-determinable.
function conditionStatusLabel(status) {
  switch (status) {
    case 'mechanical_pass': return 'Passed (passively verifiable)';
    case 'mechanical_pass_posture_only': return 'Payment-page posture only — not a PCI-DSS AOC/ROC';
    case 'mechanical_fail': return 'Not met (passively verifiable)';
    case 'cannot_determine': return 'Not determinable by passive scan';
    case 'attestation_pending': return 'Awaiting attestation';
    case 'document_pending': return 'Awaiting document';
    case 'audit_pending': return 'Awaiting audit';
    case 'not_scanned_out_of_scope': return 'Not scanned — outside declared scope';
    case 'signal_positive':
    case 'signal_negative': return 'Signal only — not scored';
    default: return 'Not determinable';
  }
}

// Render a citation object {article, subsection, lpra} as a short string.
function citationText(citation) {
  if (!citation) return '';
  if (typeof citation === 'string') return citation;
  if (citation.lpra) return citation.lpra;
  if (citation.article) return `Art. ${citation.article}${citation.subsection ? `(${citation.subsection})` : ''}`;
  return '';
}

// Group condition results by the obligation bucket the section renders.
function groupLey40Conditions(conditionResults) {
  const buckets = {
    attestation_pending: [], document_pending: [], audit_pending: [],
    not_scanned_out_of_scope: [], not_determinable: [],
  };
  for (const c of conditionResults || []) {
    const s = c.status;
    if (s === 'attestation_pending') buckets.attestation_pending.push(c);
    else if (s === 'document_pending') buckets.document_pending.push(c);
    else if (s === 'audit_pending') buckets.audit_pending.push(c);
    else if (s === 'not_scanned_out_of_scope') buckets.not_scanned_out_of_scope.push(c);
    else if (s === 'cannot_determine') buckets.not_determinable.push(c);
    // mechanical pass/fail and the MFA signal are shown in their own rows above.
  }
  return buckets;
}

// [Phase 3(c) Stage 3.5, spec §2] The stratified Ley 40 summary block: mechanical
// conformance over evaluated ONLY (null -> "not determinable", never 0%/fail),
// coverage_note verbatim, the MFA signal (never scored), and the obligation
// groupings as expandable pending buckets (never failures). Payment/7(7) posture
// and 7(6)/structural not-determinable surface via conditionStatusLabel.
function Ley40StratifiedSection({ summary, conditionResults }) {
  const mech = summary.mechanical || {};
  const buckets = groupLey40Conditions(conditionResults);
  const groups = [
    ['attestation_pending', 'Awaiting attestation', buckets.attestation_pending],
    ['document_pending', 'Awaiting document', buckets.document_pending],
    ['audit_pending', 'Awaiting audit', buckets.audit_pending],
    ['not_determinable', 'Not determinable by passive scan', buckets.not_determinable],
    ['not_scanned_out_of_scope', 'Not scanned — outside declared scope', buckets.not_scanned_out_of_scope],
  ].filter(([, , list]) => list.length > 0);
  const nullConformance = mech.conformance_pct === null || mech.conformance_pct === undefined;
  return (
    <div className="space-y-3" data-testid="ley40-stratified">
      {/* Mechanical block — conformance over evaluated only, never a blend */}
      <div className="rounded-lg border border-border/40 px-4 py-3">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Passively verifiable conditions</p>
        {nullConformance ? (
          <p className="text-sm font-medium text-muted-foreground mt-1" data-testid="ley40-conformance">
            Not determinable — no conditions could be mechanically evaluated
          </p>
        ) : (
          <p className="text-sm font-medium mt-1" data-testid="ley40-conformance">
            {mech.passed} of {mech.evaluated} mechanically-evaluated conditions passed
            <span className="font-mono ml-2 text-muted-foreground">({mech.conformance_pct}%)</span>
          </p>
        )}
        {mech.cannot_determine > 0 && (
          <p className="text-[10px] text-muted-foreground mt-0.5">
            {mech.cannot_determine} passively-checkable condition(s) could not be determined — excluded from the percentage, never counted as failures.
          </p>
        )}
        {mech.coverage_note && (
          <p className="text-[10px] text-muted-foreground italic leading-relaxed mt-2" data-testid="ley40-coverage-note">{mech.coverage_note}</p>
        )}
      </div>

      {/* Signals row (MFA, 7(8)) — corroborating signal, never scored (D-6) */}
      {summary.signals && (
        <div className="flex items-start gap-2 rounded-lg border border-border/40 px-4 py-2" data-testid="ley40-signal-mfa">
          <AlertTriangle className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
          <div>
            <p className="text-xs font-medium">MFA — {mfaSignalLabel(summary.signals.mfa)}</p>
            <p className="text-[10px] text-muted-foreground italic">Corroborating signal only, not a compliance finding — not included in any score.</p>
          </div>
        </div>
      )}

      {/* Obligation groupings — pending / not-determinable buckets, never failures */}
      {groups.length > 0 && (
        <Accordion type="multiple" className="space-y-1">
          {groups.map(([key, label, list]) => (
            <AccordionItem key={key} value={key} className="border-border/40 rounded-lg px-1">
              <AccordionTrigger className="py-2 hover:no-underline">
                <span className="text-xs text-muted-foreground">{label} ({list.length})</span>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-1 pb-2">
                  {list.map((c, i) => (
                    <div key={i} className="flex items-start gap-2 text-[10px] text-muted-foreground px-2 py-1">
                      <span className="font-mono shrink-0">{citationText(c.citation)}</span>
                      <span className="flex-1">
                        {c.sub_id}
                        {c.status === 'mechanical_pass_posture_only' && ` — ${conditionStatusLabel(c.status)}`}
                      </span>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </div>
  );
}

// [Phase 3(c) Stage 3.5, spec §3] One inclusion-reason line per displayed law,
// read from the Stage-3 fields stamped on the result doc. universal_baseline
// renders the honesty caption (WCAG two-clause, Ley 39 applicability-only), with
// NO legal basis or citation asserted (Option 1 / DEC-014).
// [Sub-stage 2, deliverable 6 / A28 resolved] `triggerQuestions` is a map
// { condition_id: question_en } fetched from the (now served) gate endpoint, so a
// profile_trigger law shows the SPECIFIC matched trigger question(s) rendered AS
// SERVED — replacing the Stage 3.5 generic line. Falls back to the generic line
// if the questions haven't loaded.
function InclusionReasonLine({ result, triggerQuestions }) {
  const reason = result.inclusion_reason;
  if (!reason) return null;
  const isWcag = result.scan_type === 'accessibility';
  if (reason === 'universal_baseline') {
    return (
      <div className="rounded-lg border border-border/40 bg-muted/10 px-3 py-2 mb-2" data-testid="inclusion-baseline">
        <p className="text-[10px] text-muted-foreground leading-relaxed">
          {isWcag ? WCAG_BASELINE_CAPTION_A : LEY39_BASELINE_CAPTION}
        </p>
        {isWcag && (
          <p className="text-[10px] text-muted-foreground leading-relaxed mt-1">{WCAG_BASELINE_CAPTION_B}</p>
        )}
      </div>
    );
  }
  if (reason === 'profile_trigger') {
    const qs = (result.inclusion_triggers || [])
      .map(cid => (triggerQuestions || {})[cid])
      .filter(Boolean);
    if (qs.length > 0) {
      return (
        <div className="rounded-lg border border-border/40 bg-muted/10 px-3 py-2 mb-2" data-testid="inclusion-reason">
          <p className="text-[10px] text-muted-foreground leading-relaxed">Included because you answered &ldquo;yes&rdquo; to:</p>
          <ul className="list-disc pl-4 mt-1 space-y-0.5">
            {qs.map((q, i) => (
              <li key={i} className="text-[10px] text-muted-foreground leading-relaxed">{q}</li>
            ))}
          </ul>
        </div>
      );
    }
    // Fallback (questions not yet loaded) — the generic Stage 3.5 line.
    return (
      <div className="rounded-lg border border-border/40 bg-muted/10 px-3 py-2 mb-2" data-testid="inclusion-reason">
        <p className="text-[10px] text-muted-foreground leading-relaxed">Included because your applicability profile places this entity in scope (Ley 40 Art. 2).</p>
      </div>
    );
  }
  let line;
  if (reason === 'per_scan_override') line = 'Included by your per-scan selection for this target.';
  else if (reason === 'admin_override') {
    const nat = result.natural_reason ? ` (would otherwise be: ${exclusionReasonLabel(result.natural_reason)})` : '';
    line = `Included by administrator override${nat}`;
  } else return null;
  return (
    <div className="rounded-lg border border-border/40 bg-muted/10 px-3 py-2 mb-2" data-testid="inclusion-reason">
      <p className="text-[10px] text-muted-foreground leading-relaxed">{line}</p>
    </div>
  );
}

// Group results by category — a law with multiple categories appears in each group
function groupByCategory(results) {
  const groups = {};
  for (const r of results) {
    const cats = (r.categories || [r.category]).filter(Boolean);
    if (cats.length === 0) cats.push('Other');
    // [DEF-F16 / STOP S-3] Ley 40 has no single blended score that may be
    // presented as compliance (DEC-008). Its legacy `compliance_score` is
    // therefore excluded from the per-category average — from BOTH the sum and
    // the divisor, so the remaining laws are not diluted by its removal. It is
    // still counted in `results` (the card says how many laws were scanned) and
    // tracked separately so the card can state its own coverage.
    const isLey40 = r.scan_type === 'cybersecurity_ley40';
    for (const cat of cats) {
      if (!groups[cat]) groups[cat] = { category: cat, results: [], totalScore: 0, scoredCount: 0, stratifiedCount: 0 };
      groups[cat].results.push(r);
      if (isLey40) {
        groups[cat].stratifiedCount += 1;
      } else {
        groups[cat].totalScore += (r.compliance_score || 0);
        groups[cat].scoredCount += 1;
      }
    }
  }
  for (const g of Object.values(groups)) {
    // NULL, never 0, when nothing in the category carries a scored percentage
    // (e.g. a Cybersecurity category holding only Ley 40). 0 would render as
    // "0% Non-compliant" — a false verdict, and precisely the over-reading this
    // change exists to prevent. Callers must branch on null.
    g.avgScore = g.scoredCount > 0 ? Math.round(g.totalScore / g.scoredCount * 10) / 10 : null;
  }
  return Object.values(groups);
}

export default function ScanResults() {
  const { scanId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [scan, setScan] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [expandedLaw, setExpandedLaw] = useState(null);
  const [catFilter, setCatFilter] = useState('');
  const [sortBy, setSortBy] = useState('category');
  const [vulnData, setVulnData] = useState(null);
  const [vulnLoading, setVulnLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('compliance');
  // [Sub-stage 2, A28] Served gate questions mapped condition_id -> question_en,
  // for the specific-trigger inclusion line. Non-fatal on failure.
  const [triggerQuestions, setTriggerQuestions] = useState({});

  useEffect(() => {
    ley40GateAPI.get()
      .then(res => {
        const map = {};
        (res.data.questions || []).forEach(q => { map[q.condition_id] = q.question_en; });
        setTriggerQuestions(map);
      })
      .catch(() => { /* gate not available -> inclusion line falls back to generic */ });
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get(`/scans/${scanId}/results`);
      setScan(res.data.scan);
      setResults(res.data.results || []);
      return res.data.scan?.status;
    } catch { navigate('/scans'); }
    return null;
  }, [scanId, navigate]);

  useEffect(() => {
    let interval;
    const init = async () => {
      setLoading(true);
      const status = await fetchData();
      setLoading(false);
      if (status === 'pending' || status === 'running') {
        setPolling(true);
        interval = setInterval(async () => {
          const s = await fetchData();
          if (s === 'completed' || s === 'failed') { setPolling(false); clearInterval(interval); }
        }, 3000);
      }
    };
    init();
    return () => { if (interval) clearInterval(interval); };
  }, [fetchData]);

  // Fetch vulnerability scan data
  useEffect(() => {
    if (!scan?.vuln_scan_id) return;
    let vulnInterval;
    const fetchVuln = async () => {
      setVulnLoading(true);
      try {
        const res = await api.get(`/vulnerability/scan/${scan.vuln_scan_id}`);
        setVulnData(res.data);
        if (res.data.status === 'completed' || res.data.status === 'failed') {
          setVulnLoading(false);
          if (vulnInterval) clearInterval(vulnInterval);
          return;
        }
      } catch { /* ignore */ }
      setVulnLoading(false);
    };
    fetchVuln();
    vulnInterval = setInterval(fetchVuln, 5000);
    return () => { if (vulnInterval) clearInterval(vulnInterval); };
  }, [scan?.vuln_scan_id]);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" /><Skeleton className="h-4 w-96" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">{[1,2,3].map(i => <Skeleton key={i} className="h-40" />)}</div>
      </div>
    );
  }

  if (!scan) return null;

  const isRunning = scan.status === 'pending' || scan.status === 'running';
  const isFailed = scan.status === 'failed';
  const isComplete = scan.status === 'completed';
  // [DEF-F14 / B-i] The banner aggregate no longer includes Ley 40 (excluded at
  // the source, scan_routes.py). It is NULL — not 0 — when no law in the scan
  // produced a scored percentage; `?? 0` would render that as a 0%
  // "Non-compliant" verdict, the same fabricated-failure trap the category cards
  // hit in §21.A.
  const hasOverall = scan.compliance_score !== null && scan.compliance_score !== undefined;
  const overallScore = hasOverall ? scan.compliance_score : 0;
  const scoreInfo = getScoreInfo(overallScore);
  const maturity = getMaturity(overallScore);
  const ScoreIcon = scoreInfo.icon;
  // [DEF-F14 / B.3] Coverage of the banner number, read from the SAME stored
  // `score_coverage` the PDF reads — never recomputed client-side (DEC-017).
  const scoreCoverage = scan.score_coverage || {};
  const coverageScored = scoreCoverage.scored_law_titles || [];
  const coverageStratified = scoreCoverage.stratified_law_titles || [];
  // [Phase 3(c) Stage 1, §5] Separate errored law scans from scored ones.
  // Scored results drive the category cards and the aggregate score exactly as
  // before (so non-error scans render unchanged); errored results are still
  // listed in the law-by-law table below, rendered as an explicit failure.
  const erroredResults = results.filter(r => r.status === 'error');
  const scoredResults = results.filter(r => r.status !== 'error');
  const categoryGroups = groupByCategory(scoredResults);
  const categories = [...new Set(results.flatMap(r => (r.categories || [r.category]).filter(Boolean)))];

  // Filtered and sorted results for the table (includes errored rows)
  let tableResults = [...results];
  if (catFilter) tableResults = tableResults.filter(r => (r.categories || [r.category]).includes(catFilter));
  if (sortBy === 'score') tableResults.sort((a, b) => (a.compliance_score || 0) - (b.compliance_score || 0));
  else if (sortBy === 'score_desc') tableResults.sort((a, b) => (b.compliance_score || 0) - (a.compliance_score || 0));
  else tableResults.sort((a, b) => (a.category || '').localeCompare(b.category || ''));

  const downloadReport = (type) => {
    const token = localStorage.getItem('vrs_token');
    window.open(`${BACKEND_URL}/api/scans/${scanId}/report/${type}?token=${token}`, '_blank');
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="scan-results-page">
      {/* ====== HEADER ====== */}
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/scans')}><ArrowLeft className="h-4 w-4" /></Button>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-semibold tracking-tight">{t('scan_results')}</h1>
            <Badge variant="outline" className={cn('text-[10px] capitalize',
              isComplete ? 'border-vrs-green/30 text-vrs-green' : isFailed ? 'border-vrs-red/30 text-vrs-red' : 'border-vrs-amber/30 text-vrs-amber'
            )}>
              {isRunning && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}{scan.status}
            </Badge>
          </div>
          <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
            <Globe className="h-3 w-3" />
            <a href={scan.target_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline flex items-center gap-1">
              {scan.target_url} <ExternalLink className="h-3 w-3" />
            </a>
            {scan.page_title && <span>{"\u2014"} {scan.page_title}</span>}
            <span className="text-xs">{"\u2022"} {formatDate(scan.created_at)}</span>
          </div>
        </div>
        {isComplete && (
          <div className="flex items-center gap-2 shrink-0">
            {(user?.role === 'regulatory' || user?.role === 'super_admin') && overallScore < 100 && (
              <Button size="sm" variant="outline" className="text-vrs-red border-vrs-red/30 hover:bg-vrs-red/10" onClick={() => navigate(`/fines/generate/${scanId}`)} data-testid="generate-fine-button">
                <Gavel className="mr-2 h-4 w-4" />Generate Fine Report
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="download-report-button">
                  <Download className="mr-2 h-4 w-4" />Download Report
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => downloadReport('full')} data-testid="download-full-report">
                  <FileText className="mr-2 h-4 w-4" />Full Compliance Report (PDF)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => downloadReport('summary')} data-testid="download-summary-report">
                  <Eye className="mr-2 h-4 w-4" />High-Level Summary (PDF)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      {/* ====== RUNNING STATE ====== */}
      {isRunning && (
        <Card className="border-border/60">
          <CardContent className="flex flex-col items-center justify-center py-16 space-y-4">
            <div className="h-16 w-16 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
            <p className="text-lg font-semibold">{t('scanning_in_progress')}</p>
            <p className="text-sm text-muted-foreground">Analyzing {scan.target_url}</p>
            <p className="text-xs text-muted-foreground">This may take 1-3 minutes depending on the number of scan types</p>
          </CardContent>
        </Card>
      )}

      {/* ====== FAILED STATE ====== */}
      {isFailed && (
        <Card className="border-vrs-red/30">
          <CardContent className="flex flex-col items-center justify-center py-12 space-y-3">
            <XCircle className="h-12 w-12 text-vrs-red" />
            <p className="text-lg font-semibold">{t('scan_failed')}</p>
            <p className="text-sm text-muted-foreground max-w-md text-center">{scan.error_message || 'An unknown error occurred'}</p>
            <Button variant="outline" onClick={() => navigate('/scans/run')}><RefreshCw className="mr-2 h-4 w-4" />{t('try_again')}</Button>
          </CardContent>
        </Card>
      )}

      {/* ====== COMPLETED: FULL DASHBOARD ====== */}
      {isComplete && (
        <>
          {/* ---- 1. TOP SECTION: Overall Summary ---- */}
          <Card className={cn('border-border/60', hasOverall ? scoreInfo.borderColor : 'border-border')}>
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center gap-6">
                <div className="flex items-center gap-4">
                  {/* [DEF-F14] No scored law -> no number, no verdict badge. */}
                  {hasOverall ? <ScoreIcon className={cn('h-14 w-14', scoreInfo.color)} /> : <Lock className="h-14 w-14 text-muted-foreground" />}
                  <div>
                    {hasOverall ? (
                      <>
                        <p className={cn('text-5xl font-bold font-mono tabular-nums', scoreInfo.color)}>{overallScore}%</p>
                        <Badge variant="outline" className={cn('text-xs mt-1', scoreInfo.color, scoreInfo.borderColor)}>{scoreInfo.label}</Badge>
                      </>
                    ) : (
                      <>
                        <p className="text-lg font-semibold text-muted-foreground" data-testid="overall-no-score">No overall score</p>
                        <Badge variant="outline" className="text-xs mt-1 text-muted-foreground border-border">No scored law in this scan</Badge>
                      </>
                    )}
                  </div>
                </div>
                <Separator orientation="vertical" className="h-16 hidden md:block" />
                <div className="flex-1 space-y-3">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-foreground">{t('overall_compliance_score')}</span>
                    {hasOverall && (
                      <Badge variant="outline" className={cn('text-[10px]', maturity.color, maturity.border)}>{maturity.level} {t('maturity_level')}</Badge>
                    )}
                  </div>
                  {hasOverall && <Progress value={overallScore} className={cn('h-3', `[&>div]:${scoreInfo.bg}`)} />}
                  {/* [DEF-F14 / B.3] Banner coverage note — states which laws the
                      score averages and names any law excluded because it is
                      stratified. Read from scan.score_coverage, the same stored
                      field the PDF reads, so the two can never disagree. */}
                  <p className="text-[10px] text-muted-foreground italic leading-relaxed" data-testid="overall-coverage-note">
                    {coverageScored.length > 0
                      ? `This score averages ${coverageScored.length} scored law${coverageScored.length !== 1 ? 's' : ''}: ${coverageScored.join(', ')}.`
                      : 'No law in this scan produced a scored percentage.'}
                    {coverageStratified.length > 0
                      ? ` ${coverageStratified.join(', ')} is assessed as a stratified result and is not included in this score — see its section below.`
                      : ''}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {categoryGroups.map(g => {
                      // [STOP S-3] avgScore is null when the category holds no
                      // scored law (Ley 40 excluded). Render a neutral stratified
                      // chip — never getScoreInfo(null), which would fall through
                      // to red "Non-compliant".
                      if (g.avgScore === null) {
                        return (
                          <Badge key={g.category} variant="outline" className="text-[10px] gap-1 text-muted-foreground border-border" data-testid="category-badge-stratified">
                            {g.category} — stratified, see details
                          </Badge>
                        );
                      }
                      const si = getScoreInfo(g.avgScore);
                      return (
                        <Badge key={g.category} variant="outline" className={cn('text-[10px] gap-1', si.borderColor, si.color)}>
                          {g.avgScore >= 80 ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                          {g.category} {g.avgScore}%
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ---- TABS ---- */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList className="bg-muted/30 border border-border/60">
              <TabsTrigger value="compliance" className="data-[state=active]:bg-accent/40" data-testid="tab-compliance">
                <ShieldCheck className="mr-2 h-4 w-4" />{t('compliance_score')}
              </TabsTrigger>
              <TabsTrigger value="vulnerability" className="data-[state=active]:bg-accent/40" data-testid="tab-vulnerability">
                <ShieldAlert className="mr-2 h-4 w-4" />Vulnerability Assessment
              </TabsTrigger>
            </TabsList>

            {/* COMPLIANCE TAB */}
            <TabsContent value="compliance" className="space-y-4">

          {/* [Sub-stage 2, deliverable 3] Provisional-applicability banner: when
              the scan's resolver run had unanswered Ley 40 triggers, tell the user
              their assessment is incomplete and route them to the /settings gate
              section to finish it. Non-blocking. */}
          {scan.resolution_provisional && (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-vrs-amber/30 bg-vrs-amber/5 px-4 py-3" data-testid="provisional-banner">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-vrs-amber mt-0.5 shrink-0" />
                <p className="text-xs text-foreground/90">
                  {t('provisional_banner').replace('{n}', String((scan.resolution || []).find(e => e.law_key === 'ley_40_2024_pr')?.unanswered_triggers?.length || ''))}
                </p>
              </div>
              <Button size="sm" variant="outline" className="h-8 text-xs shrink-0" onClick={() => navigate('/settings')} data-testid="provisional-cta">
                {t('provisional_cta')}
              </Button>
            </div>
          )}

          {/* ---- 2. MIDDLE SECTION: Category Cards ---- */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {categoryGroups.map(g => {
              // [STOP S-3] Null-safe: a category with no scored law shows no
              // percentage at all rather than a fabricated 0%.
              const noScore = g.avgScore === null;
              const si = getScoreInfo(noScore ? 0 : g.avgScore);
              const SI = noScore ? Lock : si.icon;
              return (
                <Card key={g.category} className={cn('border-border/60 transition-[border-color,box-shadow,transform] duration-200 hover:border-border/80 hover:shadow-[var(--vrs-shadow-elev-1)] hover:-translate-y-[1px]', noScore ? 'border-border' : si.borderColor)} data-testid="category-card">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <SI className={cn('h-5 w-5', noScore ? 'text-muted-foreground' : si.color)} />
                          <p className="text-sm font-semibold text-foreground">{g.category}</p>
                        </div>
                        {noScore ? (
                          <p className="text-sm font-medium text-muted-foreground" data-testid="category-score-stratified">Stratified — see details</p>
                        ) : (
                          <p className={cn('text-3xl font-bold font-mono tabular-nums', si.color)}>{g.avgScore}%</p>
                        )}
                        <p className="text-xs text-muted-foreground">{g.results.length} law{g.results.length !== 1 ? 's' : ''} scanned</p>
                        {/* [STOP S-3] Coverage statement: whenever a stratified law
                            sits in this category, say what the percentage covers, so
                            the number is never read as covering Ley 40. */}
                        {g.stratifiedCount > 0 && (
                          <p className="text-[10px] text-muted-foreground italic leading-relaxed" data-testid="category-coverage-note">
                            {noScore
                              ? 'No scored law in this category — Ley 40 is assessed as a stratified result, not a percentage. See details below.'
                              : `Percentage covers ${g.scoredCount} scored law${g.scoredCount !== 1 ? 's' : ''}; Ley 40 is shown separately as a stratified result and is not included in it.`}
                          </p>
                        )}
                      </div>
                      {noScore ? (
                        <Badge variant="outline" className="text-[10px] text-muted-foreground border-border">Stratified</Badge>
                      ) : (
                        <Badge variant="outline" className={cn('text-[10px]', si.color, si.borderColor)}>{si.label}</Badge>
                      )}
                    </div>
                    {!noScore && <Progress value={g.avgScore} className={cn('h-1.5 mt-3', `[&>div]:${si.bg}`)} />}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* [Phase 3(c) Stage 1, §5] Visible notice that one or more law scans
              failed — so a failure is never read as a pass or "not applicable". */}
          {erroredResults.length > 0 && (
            <div className="flex items-start gap-2 rounded-lg border border-vrs-amber/30 bg-vrs-amber/5 px-4 py-3" data-testid="scan-failures-banner">
              <AlertTriangle className="h-4 w-4 text-vrs-amber mt-0.5 shrink-0" />
              <p className="text-xs text-foreground/90 leading-relaxed">
                <span className="font-semibold">{erroredResults.length} law scan{erroredResults.length !== 1 ? 's' : ''} did not complete.</span>{' '}
                These are listed as <span className="text-vrs-amber font-medium">Scan Failed</span> below and are not scored — a failed scan is not a compliance finding and does not mean the law is out of scope. Re-run the scan to retry.
              </p>
            </div>
          )}

          {/* ---- 3. BOTTOM SECTION: Law-by-Law Table ---- */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground">{t('law_by_law_results')}</h2>
              <div className="flex items-center gap-2">
                <Select value={catFilter} onValueChange={v => setCatFilter(v === 'all' ? '' : v)}>
                  <SelectTrigger className="w-[150px] h-8 text-xs bg-muted/30 border-border" data-testid="results-cat-filter">
                    <Filter className="h-3 w-3 mr-1" /><SelectValue placeholder={t("all_categories")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All categories</SelectItem>
                    {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[140px] h-8 text-xs bg-muted/30 border-border">
                    <ArrowUpDown className="h-3 w-3 mr-1" /><SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="category">{t("sort_by_category")}</SelectItem>
                    <SelectItem value="score">{t("score_low_first")}</SelectItem>
                    <SelectItem value="score_desc">{t("score_high_first")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Card className="border-border/60">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="text-xs">{t("law_title")}</TableHead>
                      <TableHead className="text-xs">{t("categories")}</TableHead>
                      <TableHead className="text-xs">{t("parameters")}</TableHead>
                      <TableHead className="text-xs">{t("compliance_score")}</TableHead>
                      <TableHead className="text-xs">{t("status")}</TableHead>
                      <TableHead className="text-xs w-[100px]">{t("actions")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tableResults.map(r => {
                      // [Phase 3(c) Stage 1, §5] A failed law scan renders as an
                      // explicit "Scan Failed" row — visually distinct from a
                      // low/zero compliance score and from a not-in-scope law.
                      if (r.status === 'error') {
                        return (
                          <TableRow key={r.id || r.law_id} className="hover:bg-muted/20" data-testid="law-result-row-error">
                            <TableCell><p className="text-sm font-medium">{r.law_title || r.category}</p></TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {(r.categories || [r.category]).filter(Boolean).map((c, i) => (
                                  <Badge key={i} variant="secondary" className="text-[10px]">{c}</Badge>
                                ))}
                              </div>
                            </TableCell>
                            <TableCell><span className="text-xs text-muted-foreground">{"—"}</span></TableCell>
                            <TableCell><span className="text-xs text-muted-foreground">{"—"}</span></TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-[10px] gap-1 border-vrs-amber/30 text-vrs-amber bg-vrs-amber/10">
                                <AlertTriangle className="h-3 w-3" />Scan Failed
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <span className="text-[10px] text-muted-foreground">{scanFailureLabel(r.error_class)}</span>
                            </TableCell>
                          </TableRow>
                        );
                      }
                      const si = getScoreInfo(r.compliance_score || 0);
                      // [Phase 3(c) Stage 3.5, DEC-008] Ley 40 is rendered as a
                      // STRATIFIED assessment, not a single blended percentage.
                      // In this overview row the % / count / pass-fail badge are
                      // suppressed for the Ley 40 result and the reader is sent to
                      // the stratified detail section. Rendering only — the stored
                      // scan.compliance_score aggregate and the PDF are unchanged
                      // this sub-stage (see register §19 / DEF-F14).
                      const isLey40 = r.scan_type === 'cybersecurity_ley40';
                      // [DEF-F16] Mechanical counts for the Parameters cell. Absent
                      // summary (adapter unavailable) degrades to {} -> evaluated
                      // undefined -> the not-determinable wording below.
                      const ley40Mech = r.ley40_stratified_summary?.mechanical || {};
                      return (
                        <TableRow key={r.id || r.law_id} className="hover:bg-muted/20" data-testid="law-result-row">
                          <TableCell>
                            <p className="text-sm font-medium">{r.law_title || r.category}</p>
                            {/* [DEF-F17] Reciprocal half of the Art. 5 cross-reference.
                                Keyed on scan_type === 'privacy' (set at scan_routes.py
                                :420) because result docs do not carry law_key. Tells the
                                reader why a Ley 40 obligation is resolved on this row —
                                related by design, not a duplicate scan. Copy only: no
                                score, scan, or law is merged. */}
                            {r.scan_type === 'privacy' && (
                              <p className="text-[10px] text-muted-foreground italic mt-0.5" data-testid="ley39-art5-note">
                                Also covers the Ley 40 Art. 5 privacy-notice obligation, which is assessed here rather than under Ley 40.
                              </p>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {(r.categories || [r.category]).filter(Boolean).map((c, i) => (
                                <Badge key={i} variant="secondary" className="text-[10px]">{c}</Badge>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell>
                            {isLey40 ? (
                              // [DEF-F16, owner-ratified copy — DO NOT PARAPHRASE]
                              // "{passed} of {evaluated} passive checks · see details".
                              // Named "passive checks" so the pair can never be read as
                              // an overall Ley 40 compliance figure (the reason the blend
                              // was removed in Stage 3.5 / DEC-008). evaluated == 0 — or a
                              // missing stratified summary (adapter could not run) — renders
                              // the not-determinable wording: never "0 of 0", never a %.
                              <span className="text-xs text-muted-foreground italic" data-testid="ley40-row-stratified">
                                {ley40Mech.evaluated > 0
                                  ? `${ley40Mech.passed ?? 0} of ${ley40Mech.evaluated} passive checks · see details`
                                  : 'no passive checks determinable · see details'}
                              </span>
                            ) : (
                              <div>
                                <span className="text-sm font-mono">{r.compliant_parameters ?? 0}</span>
                                <span className="text-xs text-muted-foreground ml-1">of {r.scannable_parameters || r.total_parameters || 0} scannable</span>
                                <span className="text-xs text-muted-foreground ml-1">({r.compliant_parameters ?? 0} compliant)</span>
                                {r.total_parameters > (r.scannable_parameters || 0) && (
                                  <p className="text-[10px] text-muted-foreground/60">out of {r.total_parameters} total requirements</p>
                                )}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            {isLey40 ? (
                              <span className="text-xs text-muted-foreground">—</span>
                            ) : (
                              <span className={cn('text-sm font-bold font-mono', si.color)}>{r.compliance_score || 0}%</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {isLey40 ? (
                              <Badge variant="outline" className="text-[10px] text-muted-foreground border-border">Stratified</Badge>
                            ) : (
                              <Badge variant="outline" className={cn('text-[10px]', si.color, si.borderColor)}>{si.label}</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setExpandedLaw(expandedLaw === r.id ? null : r.id)} data-testid="view-details-button">
                                <Eye className="mr-1 h-3 w-3" />{expandedLaw === r.id ? 'Hide' : 'Details'}
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>

          {/* ---- 4. EXPANDED LAW DETAIL ---- */}
          {expandedLaw && (() => {
            const r = results.find(res => res.id === expandedLaw);
            if (!r) return null;
            return (
              <Card className="border-primary/30 bg-card/80" data-testid="law-detail-expanded">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-semibold">{r.law_title || r.category}</CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => setExpandedLaw(null)}><XCircle className="h-4 w-4" /></Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* [Phase 3(c) Stage 3.5, §3] Why this law is in your results —
                      trigger / baseline caption / override — from the resolver
                      fields stamped on the result doc. */}
                  <InclusionReasonLine result={r} triggerQuestions={triggerQuestions} />
                  {/* Privacy results */}
                  {r.category === 'Privacy' && (
                    <>
                      {r.policy_found !== undefined && (
                        <div className={cn('flex items-center gap-2 rounded-lg border px-4 py-3', r.policy_found ? 'border-vrs-green/30 bg-vrs-green/5' : 'border-vrs-red/30 bg-vrs-red/5')}>
                          {r.policy_found ? <><CheckCircle className="h-4 w-4 text-vrs-green" /><div><p className="text-sm font-medium">Privacy policy found</p>{r.policy_url && <a href={r.policy_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">{r.policy_url}</a>}</div></> : <><XCircle className="h-4 w-4 text-vrs-red" /><p className="text-sm">No privacy policy found</p></>}
                        </div>
                      )}
                      {r.parameters?.map((p, i) => (
                        <div key={i} className="flex items-start gap-3 rounded-lg border border-border/40 px-4 py-3">
                          {p.status === 'compliant' ? <CheckCircle className="h-4 w-4 text-vrs-green mt-0.5" /> : p.status === 'non_compliant' ? <XCircle className="h-4 w-4 text-vrs-red mt-0.5" /> : <AlertTriangle className="h-4 w-4 text-vrs-amber mt-0.5" />}
                          <div className="flex-1">
                            <p className="text-sm font-medium">{p.name}</p>
                            <p className="text-xs text-muted-foreground">{p.details}</p>
                            {p.found_keywords?.length > 0 && <div className="flex flex-wrap gap-1 mt-1">{p.found_keywords.slice(0, 5).map((kw, j) => <Badge key={j} variant="secondary" className="text-[9px]">{kw}</Badge>)}</div>}
                          </div>
                          <SeverityBadge type={p.status} />
                        </div>
                      ))}
                    </>
                  )}

                  {/* Accessibility results */}
                  {r.category === 'Accessibility' && (
                    <>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="rounded-lg border border-border/40 p-3 text-center"><p className="text-2xl font-bold font-mono text-vrs-red">{r.errors_count || 0}</p><p className="text-[10px] text-muted-foreground">Errors</p></div>
                        <div className="rounded-lg border border-border/40 p-3 text-center"><p className="text-2xl font-bold font-mono text-vrs-amber">{r.warnings_count || 0}</p><p className="text-[10px] text-muted-foreground">Warnings</p></div>
                        <div className="rounded-lg border border-border/40 p-3 text-center"><p className="text-2xl font-bold font-mono text-vrs-cyan">{r.notices_count || 0}</p><p className="text-[10px] text-muted-foreground">Notices</p></div>
                      </div>
                      {r.findings?.length > 0 && (
                        <Accordion type="multiple" className="space-y-1">
                          {['error', 'warning', 'notice'].map(type => {
                            const items = r.findings.filter(f => f.type === type);
                            if (!items.length) return null;
                            return (
                              <AccordionItem key={type} value={type} className="border-border/40 rounded-lg px-1">
                                <AccordionTrigger className="py-2 hover:no-underline">
                                  <div className="flex items-center gap-2"><SeverityBadge type={type} /><span className="text-sm capitalize">{type}s ({items.length})</span></div>
                                </AccordionTrigger>
                                <AccordionContent>
                                  <div className="space-y-1.5 pb-2">
                                    {items.slice(0, 50).map((f, i) => (
                                      <div key={i} className="rounded border border-border/30 bg-muted/5 p-2 space-y-1">
                                        <div className="flex items-start gap-2"><Badge variant="outline" className="text-[8px] font-mono shrink-0">{f.code}</Badge><p className="text-[11px] text-foreground/90">{f.message}</p></div>
                                        {f.selector && <p className="text-[9px] text-muted-foreground font-mono">Element: {f.selector}</p>}
                                        {f.html_snippet && <code className="block text-[9px] text-muted-foreground/70 font-mono bg-muted/20 rounded px-1.5 py-0.5 overflow-x-auto">{f.html_snippet.substring(0, 150)}</code>}
                                      </div>
                                    ))}
                                    {items.length > 50 && <p className="text-xs text-muted-foreground text-center">Showing 50 of {items.length}</p>}
                                  </div>
                                </AccordionContent>
                              </AccordionItem>
                            );
                          })}
                        </Accordion>
                      )}
                    </>
                  )}

                  {/* Cybersecurity results - General Vulnerability Assessment */}
                  {r.category === 'Cybersecurity' && r.scan_type !== 'cybersecurity_ley40' && (
                    <>
                      <div className="flex items-center gap-4 text-xs">
                        <span className="text-muted-foreground">ZAP: <Badge variant="outline" className={cn('text-[9px]', r.zap_status === 'completed' ? 'text-vrs-green border-vrs-green/30' : 'text-vrs-amber border-vrs-amber/30')}>{r.zap_status}</Badge></span>
                        <span className="text-muted-foreground">OSV: <Badge variant="outline" className={cn('text-[9px]', r.osv_status === 'completed' ? 'text-vrs-green border-vrs-green/30' : 'text-vrs-amber border-vrs-amber/30')}>{r.osv_status}</Badge></span>
                      </div>
                      <div className="grid grid-cols-4 gap-3">
                        <div className="rounded-lg border border-border/40 p-3 text-center"><p className="text-2xl font-bold font-mono text-vrs-red">{r.high_count || 0}</p><p className="text-[10px] text-muted-foreground">High</p></div>
                        <div className="rounded-lg border border-border/40 p-3 text-center"><p className="text-2xl font-bold font-mono text-vrs-amber">{r.medium_count || 0}</p><p className="text-[10px] text-muted-foreground">Medium</p></div>
                        <div className="rounded-lg border border-border/40 p-3 text-center"><p className="text-2xl font-bold font-mono text-vrs-cyan">{r.low_count || 0}</p><p className="text-[10px] text-muted-foreground">Low</p></div>
                        <div className="rounded-lg border border-border/40 p-3 text-center"><p className="text-2xl font-bold font-mono text-muted-foreground">{r.informational_count || 0}</p><p className="text-[10px] text-muted-foreground">Info</p></div>
                      </div>
                      {r.findings?.length > 0 && (
                        <Accordion type="multiple" className="space-y-1">
                          {['High', 'Medium', 'Low', 'Informational'].map(sev => {
                            const items = r.findings.filter(f => f.severity === sev);
                            if (!items.length) return null;
                            return (
                              <AccordionItem key={sev} value={sev} className="border-border/40 rounded-lg px-1">
                                <AccordionTrigger className="py-2 hover:no-underline">
                                  <div className="flex items-center gap-2"><SeverityBadge type={sev} /><span className="text-sm">{sev} ({items.length})</span></div>
                                </AccordionTrigger>
                                <AccordionContent>
                                  <div className="space-y-1.5 pb-2">
                                    {items.slice(0, 50).map((f, i) => (
                                      <div key={i} className="rounded border border-border/30 bg-muted/5 p-2 space-y-1">
                                        <div className="flex items-start justify-between gap-2">
                                          <div className="flex-1"><p className="text-[11px] font-medium">{f.name}</p>{f.description && <p className="text-[10px] text-muted-foreground">{f.description.substring(0, 200)}</p>}</div>
                                          <Badge variant="secondary" className="text-[8px] shrink-0">{f.source}</Badge>
                                        </div>
                                        {f.solution && <p className="text-[10px] text-vrs-green"><span className="font-semibold">Fix:</span> {f.solution.substring(0, 150)}</p>}
                                      </div>
                                    ))}
                                  </div>
                                </AccordionContent>
                              </AccordionItem>
                            );
                          })}
                        </Accordion>
                      )}
                    </>
                  )}

                  {/* Cybersecurity results - Ley 40-2024 */}
                  {r.scan_type === 'cybersecurity_ley40' && (
                    <>
                      {/* [Phase 3(c) Stage 3.5, §2] Full stratified view: mechanical
                          conformance over evaluated only (null -> not determinable,
                          never 0%/fail), coverage_note verbatim, MFA signal (never
                          scored), and pending obligation groupings. Replaces the
                          Stage 1 partial header. No blended Ley 40 percentage. */}
                      {r.ley40_stratified_summary?.mechanical ? (
                        <Ley40StratifiedSection summary={r.ley40_stratified_summary} conditionResults={r.ley40_condition_results} />
                      ) : (
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                          Passively verifiable checks only — not an overall Ley 40 compliance figure
                        </p>
                      )}
                      {/* Mechanical per-check detail (legacy positional checks) */}
                      <div className="space-y-2">
                        {(r.results || r.findings || []).filter(p => p.parameter_name).map((p, i) => (
                          <div key={i} className="rounded-lg border border-border/40 px-4 py-3">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex items-start gap-3 flex-1">
                                <div className="mt-0.5">
                                  {p.status === 'compliant' && <CheckCircle className="h-4 w-4 text-vrs-green" />}
                                  {p.status === 'partial' && <AlertTriangle className="h-4 w-4 text-vrs-amber" />}
                                  {p.status === 'non_compliant' && <XCircle className="h-4 w-4 text-vrs-red" />}
                                  {p.status === 'not_applicable' && <span className="text-xs text-muted-foreground">N/A</span>}
                                  {p.status === 'unable_to_determine' && <AlertTriangle className="h-4 w-4 text-muted-foreground" />}
                                </div>
                                <div className="flex-1">
                                  <p className="text-sm font-medium">{p.parameter_name}</p>
                                  {p.requirement_refs && <p className="text-[10px] text-muted-foreground">{p.requirement_refs.join(', ')}</p>}
                                  {p.findings && typeof p.findings === 'object' && Object.keys(p.findings).length > 0 && (
                                    <div className="mt-1 space-y-0.5">
                                      {Object.entries(p.findings).filter(([k, v]) => v !== '' && v !== null && v !== undefined && k !== 'note').map(([k, v]) => (
                                        <p key={k} className="text-[10px] text-muted-foreground">
                                          <span className="font-mono">{k}:</span> {typeof v === 'boolean' ? (v ? 'Yes' : 'No') : Array.isArray(v) ? v.join(', ') : String(v).substring(0, 150)}
                                        </p>
                                      ))}
                                      {p.findings.note && <p className="text-[10px] text-muted-foreground italic mt-1">{p.findings.note}</p>}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                {p.score !== undefined && <span className={cn('text-xs font-mono font-bold', p.score >= 80 ? 'text-vrs-green' : p.score >= 50 ? 'text-vrs-amber' : 'text-vrs-red')}>{p.score}%</span>}
                                <SeverityBadge type={p.status} />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Privacy Notice cross-reference note.
                          [DEF-F17] Made explicit and directional: the old copy said
                          Art. 5 is "evaluated separately ... above", which read as a
                          second, duplicate scan and pointed at a row whose position is
                          not stable (the table is user-sortable — never say
                          above/below). Now it names Ley 39-2012 as the single place the
                          obligation is assessed and says why it is not re-scanned here.
                          Copy/clarity only — no scores, scans, or laws are merged. */}
                      <div className="flex items-start gap-2 rounded-lg border border-primary/20 bg-primary/5 px-4 py-3" data-testid="ley40-art5-crossref">
                        <CheckCircle className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                        <div>
                          <p className="text-xs font-medium text-foreground">Privacy Notice (Art. 5) — assessed under Ley 39-2012, not re-scanned here</p>
                          {/* [Phase 3(c) Stage 1, D-5] "8th externally scannable" coverage claim removed. */}
                          <p className="text-[10px] text-muted-foreground leading-relaxed">
                            The Ley 40 Art. 5 privacy-notice obligation is assessed by the Ley 39-2012 (Privacy Policy) scan, which appears as its own law in this report. It is evaluated once, there — not re-scanned under Ley 40 and not counted twice.
                          </p>
                        </div>
                      </div>

                      {/* Non-scannable requirements */}
                      {r.non_scannable_requirements?.length > 0 && (
                        <Accordion type="single" collapsible className="space-y-1">
                          <AccordionItem value="non-scannable" className="border-border/40 rounded-lg px-1">
                            <AccordionTrigger className="py-2 hover:no-underline">
                              <div className="flex items-center gap-2">
                                <Lock className="h-3 w-3 text-muted-foreground" />
                                {/* [Phase 3(c) Stage 1, D-5] count from actual list, not hardcoded "11". */}
                                <span className="text-xs text-muted-foreground">{r.non_scannable_requirements.length} requirements require internal audit (not externally scannable)</span>
                              </div>
                            </AccordionTrigger>
                            <AccordionContent>
                              <div className="space-y-1 pb-2">
                                {r.non_scannable_requirements.map((ns, i) => (
                                  <div key={i} className="flex items-center gap-2 text-[10px] text-muted-foreground px-2 py-1">
                                    <Lock className="h-3 w-3 shrink-0" />
                                    <span className="font-medium">{ns.requirement}</span>
                                    <span className="text-muted-foreground/60">- {ns.reason}</span>
                                  </div>
                                ))}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion>
                      )}
                    </>
                  )}

                  {/* Generic law results (HIPAA, GLBA, CMMC, etc.) */}
                  {r.scan_type === 'generic_law' && r.parameters?.length > 0 && (
                    <div className="space-y-2">
                      {/* External Scan Only disclaimer — generic_law results only */}
                      <div className="flex items-start gap-2 rounded-lg border border-vrs-amber/30 bg-vrs-amber/5 px-4 py-3">
                        <AlertTriangle className="h-4 w-4 text-vrs-amber mt-0.5 shrink-0" />
                        <p className="text-xs text-foreground/90 leading-relaxed">
                          <span className="font-semibold">External Scan Only:</span> This assessment evaluates {r.scannable_parameters || 0} of {r.total_parameters || 0} total requirements that are verifiable from external public-facing assets. The remaining {r.not_scannable_count || (r.total_parameters || 0) - (r.scannable_parameters || 0)} requirements cannot be assessed remotely and require an internal compliance audit by a qualified professional.
                        </p>
                      </div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        Parameters ({r.compliant_parameters || 0} compliant of {r.scannable_parameters || 0} scannable, {r.total_parameters || 0} total)
                      </p>
                      {r.parameters.map((p, i) => (
                        <div key={i} className="flex items-start gap-3 rounded-lg border border-border/40 px-4 py-3">
                          <div className="mt-0.5">
                            {p.status === 'compliant' && <CheckCircle className="h-4 w-4 text-vrs-green" />}
                            {p.status === 'partial' && <AlertTriangle className="h-4 w-4 text-vrs-amber" />}
                            {p.status === 'non_compliant' && <XCircle className="h-4 w-4 text-vrs-red" />}
                            {p.status === 'requires_internal_audit' && <Lock className="h-4 w-4 text-muted-foreground" />}
                            {p.status === 'unable_to_determine' && <AlertTriangle className="h-4 w-4 text-muted-foreground" />}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium">{p.parameter_name}</p>
                            <p className="text-xs text-muted-foreground">{p.details}</p>
                            {p.findings?.keywords_found?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {p.findings.keywords_found.slice(0, 5).map((kw, j) => (
                                  <Badge key={j} variant="secondary" className="text-[9px]">{kw}</Badge>
                                ))}
                              </div>
                            )}
                          </div>
                          <Badge variant="outline" className={cn('text-[10px] shrink-0',
                            p.status === 'compliant' ? 'border-vrs-green/30 text-vrs-green' :
                            p.status === 'partial' ? 'border-vrs-amber/30 text-vrs-amber' :
                            p.status === 'non_compliant' ? 'border-vrs-red/30 text-vrs-red' :
                            p.status === 'requires_internal_audit' ? 'border-muted-foreground/30 text-muted-foreground' :
                            'text-muted-foreground'
                          )}>
                            {p.status === 'requires_internal_audit' ? 'Internal Audit' : p.status?.replace(/_/g, ' ')}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Generic fallback for unknown scan types */}
                  {!['Privacy', 'Accessibility', 'Cybersecurity'].includes(r.category) && r.scan_type !== 'generic_law' && r.scan_type !== 'cybersecurity_ley40' && r.findings?.length > 0 && (
                    <div className="space-y-2">
                      {r.findings.slice(0, 50).map((f, i) => (
                        <div key={i} className="rounded border border-border/30 bg-muted/5 p-2">
                          <p className="text-xs">{f.message || f.name || f.parameter || JSON.stringify(f).substring(0, 200)}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Remediation footer */}
                  <Separator />
                  <div className="rounded-lg border border-border/30 bg-muted/10 px-4 py-3 space-y-2">
                    {r.disclaimer && <p className="text-[10px] text-muted-foreground italic">{r.disclaimer}</p>}
                    <p className="text-[10px] text-muted-foreground">
                      For assistance with remediation of identified issues, contact{' '}
                      <a href="https://www.cybercheck.one" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">CyberCheck LLC at www.cybercheck.one</a>
                    </p>
                  </div>
                </CardContent>
              </Card>
            );
          })()}

            {/* [Phase 3(c) Stage 3.5, §3 / memo §VI] "Not scanned in this
                assessment" — every EXCLUDED law from the resolver manifest, with
                its neutral reason, so absence is explained rather than silent. */}
            {Array.isArray(scan.resolution) && scan.resolution.filter(e => e.decision === 'excluded').length > 0 && (
              <Card className="border-border/40 bg-muted/5 mt-4" data-testid="not-scanned-section">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-muted-foreground">Not scanned in this assessment</CardTitle>
                </CardHeader>
                <CardContent className="space-y-1.5">
                  {scan.resolution.filter(e => e.decision === 'excluded').map((e, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs" data-testid="not-scanned-row">
                      <Lock className="h-3 w-3 text-muted-foreground/60 mt-0.5 shrink-0" />
                      <div>
                        <span className="font-medium">{e.title || lawKeyDisplayName(e.law_key)}</span>
                        <span className="text-muted-foreground"> — {exclusionReasonLabel(e.exclusion_reason)}</span>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            </TabsContent>

            {/* VULNERABILITY ASSESSMENT TAB */}
            <TabsContent value="vulnerability" className="space-y-4">
              {/* Info banner */}
              <div className="rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 flex items-center justify-between">
                <p className="text-xs text-muted-foreground">Vulnerability Assessment is currently available to all users. In a future release, this feature will be part of the Compliance Plus plan.</p>
              </div>

              {/* Overall risk */}
              {vulnData ? (
                <>
                  <Card className={cn('border-border/60', vulnData.overall_risk === 'critical' ? 'border-vrs-red/40' : vulnData.overall_risk === 'high' ? 'border-vrs-red/30' : vulnData.overall_risk === 'medium' ? 'border-vrs-amber/30' : 'border-vrs-green/30')}>
                    <CardContent className="p-5 flex items-center gap-4">
                      <ShieldAlert className={cn('h-8 w-8', vulnData.overall_risk === 'critical' || vulnData.overall_risk === 'high' ? 'text-vrs-red' : vulnData.overall_risk === 'medium' ? 'text-vrs-amber' : 'text-vrs-green')} />
                      <div>
                        <p className="text-sm font-semibold text-foreground">Overall Risk Level</p>
                        <Badge variant="outline" className={cn('text-xs capitalize mt-1', vulnData.overall_risk === 'critical' || vulnData.overall_risk === 'high' ? 'border-vrs-red/30 text-vrs-red' : vulnData.overall_risk === 'medium' ? 'border-vrs-amber/30 text-vrs-amber' : 'border-vrs-green/30 text-vrs-green')}>
                          {vulnData.overall_risk}
                        </Badge>
                      </div>
                      <div className="ml-auto text-right">
                        <p className="text-xs text-muted-foreground">{vulnData.status === 'completed' ? `Completed in ${vulnData.total_duration}s` : vulnData.status}</p>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Engine cards grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {[
                      { key: 'sslyze', label: 'TLS/SSL Analysis', icon: Lock },
                      { key: 'nmap_tcp', label: 'Open Ports (TCP)', icon: Globe },
                      { key: 'nmap_udp', label: 'Open Ports (UDP)', icon: Globe },
                      { key: 'zap_passive', label: 'Passive Web Scan', icon: ShieldCheck },
                      { key: 'zap_active', label: 'Active Web Scan', icon: ShieldAlert },
                      { key: 'openvas', label: 'Deep Vulnerability Scan', icon: ShieldX },
                    ].map(eng => {
                      const data = vulnData.engines?.[eng.key];
                      if (!data) return (
                        <Card key={eng.key} className="border-border/60 opacity-50"><CardContent className="p-5"><div className="flex items-center gap-3"><eng.icon className="h-5 w-5 text-muted-foreground" /><p className="text-sm font-medium">{eng.label}</p></div><Badge variant="secondary" className="text-[10px] mt-2">No data</Badge></CardContent></Card>
                      );
                      const statusColor = data.status === 'completed' ? 'border-vrs-green/30' : data.status === 'coming_soon' ? 'border-muted-foreground/20' : data.status === 'failed' || data.status === 'timeout' ? 'border-vrs-red/30' : 'border-vrs-amber/30';
                      const riskColor = data.risk_level === 'critical' || data.risk_level === 'high' ? 'text-vrs-red' : data.risk_level === 'medium' ? 'text-vrs-amber' : data.risk_level === 'low' ? 'text-vrs-green' : 'text-muted-foreground';
                      return (
                        <Card key={eng.key} className={cn('border-border/60', statusColor)} data-testid={`vuln-engine-${eng.key}`}>
                          <CardContent className="p-5 space-y-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2"><eng.icon className="h-5 w-5 text-primary" /><p className="text-sm font-semibold">{eng.label}</p></div>
                              <Badge variant="outline" className={cn('text-[10px] capitalize', data.status === 'completed' ? 'border-vrs-green/30 text-vrs-green' : data.status === 'coming_soon' ? 'text-muted-foreground' : 'border-vrs-red/30 text-vrs-red')}>
                                {data.status === 'coming_soon' ? 'Coming Soon' : data.status}
                              </Badge>
                            </div>
                            {data.status === 'completed' && (
                              <>
                                <div className="flex items-center gap-3">
                                  <Badge variant="outline" className={cn('text-[10px] capitalize', riskColor)}>{data.risk_level} risk</Badge>
                                  <span className="text-xs text-muted-foreground">{data.findings?.length || 0} findings</span>
                                  <span className="text-xs text-muted-foreground">{data.duration_seconds}s</span>
                                </div>
                                {data.findings?.length > 0 && (
                                  <Accordion type="single" collapsible>
                                    <AccordionItem value="findings" className="border-border/30">
                                      <AccordionTrigger className="py-2 hover:no-underline text-xs">{t('findings')} ({data.findings.length})</AccordionTrigger>
                                      <AccordionContent>
                                        <div className="space-y-1.5">
                                          {data.findings.slice(0, 20).map((f, i) => (
                                            <div key={i} className="rounded border border-border/30 bg-muted/5 p-2">
                                              <div className="flex items-start gap-2">
                                                <Badge variant="outline" className={cn('text-[9px] shrink-0', f.severity === 'critical' || f.severity === 'high' ? 'border-vrs-red/30 text-vrs-red' : f.severity === 'medium' ? 'border-vrs-amber/30 text-vrs-amber' : f.severity === 'low' ? 'border-vrs-cyan/30 text-vrs-cyan' : 'text-muted-foreground')}>{f.severity}</Badge>
                                                <div className="flex-1"><p className="text-xs font-medium">{f.title}</p>{f.description && <p className="text-[10px] text-muted-foreground mt-0.5">{f.description.substring(0, 200)}</p>}{f.remediation && <p className="text-[10px] text-vrs-green mt-0.5">Fix: {f.remediation.substring(0, 150)}</p>}</div>
                                              </div>
                                            </div>
                                          ))}
                                          {data.findings.length > 20 && <p className="text-xs text-muted-foreground text-center">+{data.findings.length - 20} more</p>}
                                        </div>
                                      </AccordionContent>
                                    </AccordionItem>
                                  </Accordion>
                                )}
                              </>
                            )}
                            {data.status === 'failed' && data.error && <p className="text-xs text-muted-foreground">{data.error.substring(0, 200)}</p>}
                            {data.status === 'coming_soon' && <p className="text-xs text-muted-foreground">{data.summary}</p>}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>

                  {/* Summary counts */}
                  {(() => {
                    const allFindings = Object.values(vulnData.engines || {}).flatMap(e => e.findings || []);
                    const counts = { critical: 0, high: 0, medium: 0, low: 0, info: 0 };
                    allFindings.forEach(f => { counts[f.severity] = (counts[f.severity] || 0) + 1; });
                    return (
                      <Card className="border-border/60">
                        <CardContent className="p-5">
                          <p className="text-xs font-semibold tracking-wide uppercase text-muted-foreground mb-3">Finding Summary</p>
                          <div className="grid grid-cols-5 gap-3">
                            {[['Critical', counts.critical, 'text-vrs-red'], ['High', counts.high, 'text-vrs-red'], ['Medium', counts.medium, 'text-vrs-amber'], ['Low', counts.low, 'text-vrs-cyan'], ['Info', counts.info, 'text-muted-foreground']].map(([label, count, color]) => (
                              <div key={label} className="text-center rounded-lg border border-border/40 p-3">
                                <p className={cn('text-xl font-bold font-mono', color)}>{count}</p>
                                <p className="text-[10px] text-muted-foreground">{label}</p>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })()}
                </>
              ) : vulnLoading ? (
                <Card className="border-border/60">
                  <CardContent className="flex flex-col items-center justify-center py-16 space-y-4">
                    <div className="h-12 w-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
                    <p className="text-sm font-semibold">Vulnerability scan in progress...</p>
                    <p className="text-xs text-muted-foreground">Running SSLyze, Nmap, and security analysis engines</p>
                  </CardContent>
                </Card>
              ) : (
                <Card className="border-border/60">
                  <CardContent className="flex flex-col items-center justify-center py-16 space-y-3">
                    <ShieldAlert className="h-10 w-10 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">No vulnerability scan data available</p>
                    <p className="text-xs text-muted-foreground">Vulnerability scans run automatically after compliance scans complete.</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
